/**
 * Shown until an actor exists: the provider's own sign-in, or the seeded-user
 * picker in AUTH_MODE=dev.
 *
 * The provider button is a redirect to `/api/auth/login` — the server owns the
 * flow (ADR-005). The SPA has no client id, no authority and no token.
 */
import { useEffect, useState } from 'react';
import { LogIn, ShieldCheck, UserRound } from 'lucide-react';
import { apiFetch } from '@/api/client';
import { endpoints } from '@/api/endpoints';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button, Input, PopoverItem } from '@/kit';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { envOr } from '@/lib/env';
import { signInLabel } from './config';
import { useAuth } from './AuthProvider';
import { useMeta } from '@/app/meta';
import { BrandLogo } from '@/components/BrandLogo';

/** Tenant-neutral: the deployed org sets VITE_ORG_NAME; /api/meta will own this at runtime (SALVAGE-REVIEW §5). */
const ORG_NAME = envOr(import.meta.env.VITE_ORG_NAME, 'Capability Roadmap');

/** AUTH_MODE=dev: POST the chosen seeded user, then remember it for this browser. */
async function signInAsDevUser(
  userId: string,
  remember: (id: string) => void,
  onError: (message: string | null) => void,
): Promise<void> {
  onError(null);
  try {
    await apiFetch(endpoints.devLogin, { method: 'POST', body: { userId } });
    remember(userId);
  } catch (err) {
    onError(err instanceof Error ? err.message : 'Dev sign-in failed');
  }
}

interface DevUser {
  id: string;
  displayName: string;
  roles?: string[];
  department?: string | null;
}

function normaliseDevUsers(raw: unknown): DevUser[] {
  const list = Array.isArray(raw) ? raw : ((raw as { users?: unknown[] })?.users ?? []);
  return (list as Record<string, unknown>[]).map((u) => ({
    id: String(u.id ?? u.userId ?? u.key ?? ''),
    displayName: String(u.displayName ?? u.display_name ?? u.name ?? u.email ?? 'Seeded user'),
    roles: normaliseRoles(u.roles),
    department: (u.department as string | undefined) ?? (u.scopeName as string | undefined) ?? null,
  }));
}

function normaliseRoles(raw: unknown): string[] | undefined {
  if (!Array.isArray(raw)) return undefined;
  return raw.map((r) => (typeof r === 'string' ? r : String((r as { roleCode?: string }).roleCode ?? '')));
}

export function SignInScreen() {
  const { mode, signIn, setDevUser, problems } = useAuth();
  const meta = useMeta();
  const [users, setUsers] = useState<DevUser[] | null>(null);
  const [loading, setLoading] = useState(mode === 'dev');
  const [manual, setManual] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (mode !== 'dev') return;
    let cancelled = false;
    apiFetch<unknown>(endpoints.devUsers)
      .then((raw) => {
        if (!cancelled) setUsers(normaliseDevUsers(raw).filter((u) => u.id));
      })
      .catch(() => {
        if (!cancelled) setUsers([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [mode]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <Card className="w-full max-w-md">
        <CardHeader>
          <BrandLogo logos={meta.logos} kind="lockup" appName={meta.appName} className="mb-3 max-h-12 max-w-60 object-contain" />
          <div className="mb-2 flex items-center gap-2 text-primary">
            <ShieldCheck className="h-5 w-5" aria-hidden />
            <span className="id-mono text-[length:var(--t-th)] uppercase tracking-widest">{ORG_NAME}</span>
          </div>
          <CardTitle className="text-2xl">Capability Investment Governance</CardTitle>
          <CardDescription>
            {mode === 'dev'
              ? 'Development sign-in. Pick the seeded user you want the API to act as — permissions are enforced server-side exactly as they are for a real sign-in.'
              : 'Sign in with your organization account to open your capabilities.'}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {mode !== 'dev' ? (
            <Button variant="primary" className="w-full" onClick={signIn}>
              <LogIn aria-hidden />
              {signInLabel(mode)}
            </Button>
          ) : loading ? (
            <div className="space-y-2">
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
            </div>
          ) : users && users.length > 0 ? (
            <ul className="space-y-2">
              {users.map((u) => (
                <li key={u.id}>
                  <PopoverItem onSelect={() => void signInAsDevUser(u.id, setDevUser, setError)} className="border border-border">
                    <UserRound className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-medium">{u.displayName}</span>
                      <span className="id-mono block truncate text-[length:var(--t-th)] text-muted-foreground">
                        {[u.department, u.roles?.join(' · ')].filter(Boolean).join(' — ')}
                      </span>
                    </span>
                  </PopoverItem>
                </li>
              ))}
            </ul>
          ) : (
            <form
              className="space-y-3"
              onSubmit={(e) => {
                e.preventDefault();
                if (manual.trim()) void signInAsDevUser(manual.trim(), setDevUser, setError);
              }}
            >
              <div className="space-y-1">
                <Label htmlFor="dev-user">Seeded user</Label>
                <Input
                  className="w-full"
                  id="dev-user"
                  value={manual}
                  onChange={(e) => setManual(e.target.value)}
                  placeholder="00000000-0000-0000-0000-000000000000"
                  autoComplete="off"
                />
                <p className="text-[length:var(--t-th)] text-muted-foreground">
                  The API has not published its seeded-user list yet. Paste a seeded user id; the API signs you in as that
                  user and applies their roles.
                </p>
              </div>
              <Button type="submit" variant="primary" className="w-full" disabled={!manual.trim()}>
                Continue
              </Button>
            </form>
          )}
          {error ? <p className="text-sm text-destructive">{error}</p> : null}
          {problems.length > 0 ? (
            <div className="rounded-md border border-destructive/40 bg-destructive/5 p-3">
              <p className="text-sm font-medium text-destructive">This instance is misconfigured</p>
              <ul className="mt-1 list-disc space-y-1 pl-4 text-[length:var(--t-th)] text-muted-foreground">
                {problems.map((p) => (
                  <li key={p}>{p}</li>
                ))}
              </ul>
            </div>
          ) : null}
        </CardContent>
      </Card>
    </div>
  );
}
